import { TypedDocumentNode, gql } from '@apollo/client';
import { faGift } from '@fortawesome/pro-solid-svg-icons';
import { isBefore } from 'date-fns';
import { useState } from 'react';
import styled from 'styled-components';

import { FootballManagerTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import { useTickerContext } from '@sorare/core/src/contexts/ticker';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { randomizedPollInterval } from '@sorare/core/src/lib/gql';

import { DailyClaimButtonOnboarding } from '../DailyClaimButtonOnboarding';
import { DailyClaimDialog } from '../DailyClaimDialog';
import {
  MLBDailyClaimButtonQuery,
  MLBDailyClaimButtonQueryVariables,
} from './__generated__/index.graphql';

const ButtonWithBadge = styled.div`
  padding-right: var(--intermediate-unit);
`;

const query = gql`
  query MLBDailyClaimButtonQuery {
    currentUser {
      slug
      baseballUserProfile {
        id
        dailyClaimTasks {
          id
          aasmState
          readyAt
        }
      }
    }
  }
` as TypedDocumentNode<
  MLBDailyClaimButtonQuery,
  MLBDailyClaimButtonQueryVariables
>;

const POLL_INTERVAL = randomizedPollInterval(5);

export const DailyClaimButton = () => {
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const { now } = useTickerContext();

  const { data } = useQuery(query, {
    pollInterval: POLL_INTERVAL,
  });

  const tasks = data?.currentUser?.baseballUserProfile?.dailyClaimTasks || [];
  const remainingTasks = tasks.filter(
    t => t.aasmState !== FootballManagerTaskState.CLAIMED
  );

  const hasClaimTaskReady = remainingTasks.some(
    t =>
      [
        FootballManagerTaskState.READY,
        FootballManagerTaskState.COMPLETED,
      ].includes(t.aasmState) &&
      t.readyAt &&
      isBefore(t.readyAt, now)
  );

  return (
    <>
      <DailyClaimButtonOnboarding
        claimDialogOpen={claimDialogOpen}
        hasClaimTaskReady={hasClaimTaskReady}
        setClaimDialogOpen={setClaimDialogOpen}
      />
      <OnboardingHighlightWrapper
        highlightName="DailyClaimButton"
        wrap={remainingTasks.length > 0}
      >
        {remainingTasks.length > 0 && (
          <ConditionalWrapper
            Wrapper={ButtonWithBadge}
            wrap={hasClaimTaskReady}
          >
            <Badge
              color="red"
              badgeContent={1}
              overlap="circular"
              invisible={!hasClaimTaskReady}
              top={1.5}
              right={1}
            >
              <IconButton
                icon={faGift}
                color="transparent"
                onClick={() => setClaimDialogOpen(true)}
              />
            </Badge>
          </ConditionalWrapper>
        )}
      </OnboardingHighlightWrapper>
      <DailyClaimDialog
        open={claimDialogOpen}
        onClose={() => {
          setClaimDialogOpen(false);
          if (!lifecycle?.sawMLBDailyClaimButton) {
            updateLifecycle(LIFECYCLE.sawMLBDailyClaimButton, true);
          }
        }}
      />
    </>
  );
};
